import React, { useCallback, useEffect, useRef, useState } from 'react';

export type AppDialogOptions = {
  title?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
};

type DialogRequest = Required<Omit<AppDialogOptions, 'danger'>> & {
  kind: 'alert' | 'confirm';
  message: string;
  danger: boolean;
  resolve: (value: boolean) => void;
};

/** App-styled, promise-based replacement for window.alert / window.confirm. Render `dialog` once in the component tree. */
export function useAppDialog() {
  const [request, setRequest] = useState<DialogRequest | null>(null);

  const open = useCallback((kind: DialogRequest['kind'], message: string, options: AppDialogOptions = {}) =>
    new Promise<boolean>((resolve) => {
      setRequest({
        kind,
        message,
        title: options.title || (kind === 'confirm' ? 'Please confirm' : 'Notice'),
        confirmLabel: options.confirmLabel || (kind === 'confirm' ? 'Confirm' : 'OK'),
        cancelLabel: options.cancelLabel || 'Cancel',
        danger: Boolean(options.danger),
        resolve,
      });
    }), []);

  const alert = useCallback(async (message: string, options?: AppDialogOptions) => {
    await open('alert', message, options);
  }, [open]);

  const confirm = useCallback((message: string, options?: AppDialogOptions) => open('confirm', message, options), [open]);

  const close = useCallback((value: boolean) => {
    setRequest((current) => {
      current?.resolve(value);
      return null;
    });
  }, []);

  const dialog = request ? <AppDialog request={request} onClose={close} /> : null;
  return { alert, confirm, dialog };
}

const AppDialog: React.FC<{ request: DialogRequest; onClose: (value: boolean) => void }> = ({ request, onClose }) => {
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    confirmRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <div className="modal-overlay app-dialog-overlay" onClick={() => onClose(false)}>
      <div
        className="modal app-dialog"
        role={request.kind === 'confirm' ? 'alertdialog' : 'dialog'}
        aria-modal="true"
        aria-labelledby="app-dialog-title"
        aria-describedby="app-dialog-message"
        onClick={(event) => event.stopPropagation()}
      >
        <h3 id="app-dialog-title">{request.title}</h3>
        <p id="app-dialog-message" className="app-dialog-message">{request.message}</p>
        <div className="form-actions app-dialog-actions">
          {request.kind === 'confirm' && (
            <button type="button" className="btn-secondary" onClick={() => onClose(false)}>{request.cancelLabel}</button>
          )}
          <button
            type="button"
            ref={confirmRef}
            className={request.danger ? 'btn-danger' : 'btn-primary'}
            onClick={() => onClose(true)}
          >
            {request.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};
